import type { Theme } from '@personalizehq/schemas/ir';

// THE WEB HALF OF THE FONT-REGISTRATION API (SPEC §4.1). Fonts are BUNDLED, never system:
// Android ships no Arial and silently substitutes Roboto, so a system-font policy would make
// glyph advances differ BY DESIGN and end `line_count` as an exact conformance gate forever.
//
// LAYOUT WAITS FOR REGISTRATION. Measuring with a fallback font and repainting once the real one
// arrives would move every wrap point — the same reflow-on-load that `aspect_ratio` exists to
// prevent for images, applied to text. This is why mount() exposes `ready`.

/** THE FALLBACK, AND WHY IT HAS TO EXIST.
 *
 *  A token's `family` was emitted BARE — `font: 700 24px Inter`. When the family does not resolve
 *  (no `src` uploaded, and no machine has "Inter" installed by default), CSS falls back to the
 *  document's default generic — and inside the renderer's `all: initial` reset that default is
 *  **serif**. So every headline in the dashboard canvas and on the phone preview rendered in TIMES:
 *  the seed theme names Inter, nothing loads Inter, and the reset turns the miss into a serif.
 *
 *  A UI font that goes missing should degrade to the platform's UI font, never to Times New Roman.
 *
 *  IT MUST BE THE SAME STRING IN THE PAINTER AND THE MEASURER. `measure-text.ts` sets `ctx.font`
 *  from this too — measure with one family and paint with another and every wrap point moves, which
 *  is exactly the reflow this file exists to prevent. Hence one helper, two callers.
 *
 *  When a font IS registered (the `src` path below), the family resolves and the fallback never
 *  applies — so this changes nothing about a correctly-bundled theme, and it is invisible to
 *  conformance, whose fixtures all name a family that resolves. */
const FALLBACK = 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/** Quoted, because a family with a space (`"Helvetica Neue"`) is not a valid bare CSS ident. */
export const fontStack = (family: string): string => `"${family}", ${FALLBACK}`;

/** Registers every theme font that names a `src` resolving in the asset map.
 *
 *  A font that FAILS to load degrades to the fallback family and layout proceeds. A missing
 *  font must never hang an experience — a silently plain modal beats a blank one. */
export async function loadFonts(
  theme: Theme,
  assets: Readonly<Record<string, string>>
): Promise<void> {
  const faces: FontFace[] = [];

  for (const token of Object.values(theme.fonts)) {
    const url = token.src ? assets[token.src] : undefined;
    if (!url) continue; // no src, or no asset entry: nothing to register (today's whole corpus)
    faces.push(
      new FontFace(token.family, `url(${url})`, {
        weight: String(token.weight),
        display: 'block',
      })
    );
  }

  await Promise.all(
    faces.map(async (face) => {
      try {
        await face.load();
        document.fonts.add(face);
      } catch {
        // Degrade, never hang. The fallback family measures differently, which is precisely
        // what the conformance suite would catch if this ever fired on a real corpus.
      }
    })
  );
}
